import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import useAuth from './useAuth'
import { useDispatch, useSelector } from 'react-redux'
import {
    SET_CONFIG_ANALYZER_MEASUREMENT,
    SET_CONFIG_UPDATE_ANALYZER_MEASUREMENT,
    SET_END_DATE_ANALYZER_DATE_TIME,
    SET_LIMIT_ANALYZER_MEASUREMENT,
    SET_ORDER_ANALYZER_MEASUREMENT,
    SET_PAGE_ANALYZER_MEASUREMENT,
    SET_SEARCH_ANALYZER_MEASUREMENT,
    SET_START_DATE_ANALYZER_DATE_TIME,
} from 'app/redux/actions/AnalyzerDateTimeAction'
import { useDashboardConfig } from 'app/contexts/DashboardConfigContext'

export const useGetCriteriaLabel = () => {
    const { config } = useDashboardConfig()
    const cacheRef = useRef({}) // cache persists between renders

    const handleCriteria = (conf, name, value) => {
        if (!conf || !name || value == null) return null

        const n = parseFloat(value)
        const cache = cacheRef.current

        // Hit cache
        if (cache[name]) {
            if (!cache[name].levels || cache[name].levels.length === 0) {
                return null
            }
            const c = cache[name]
            for (const v of c.levels) {
                if (v.gte <= n && n <= v.lte) {
                    return v
                }
            }
        }

        // Search in config
        for (const c of conf) {
            if (c.name === name) {
                cache[name] = c
                for (const v of c.levels) {
                    if (v.gte <= n && n <= v.lte) {
                        return v
                    }
                }
            }
        }

        // No match
        cache[name] = []
        return null
    }

    const format = (measurements) => {
        if (!config)
            return {
                label: '--',
                order: 1,
                level: -1,
            }

        const conf = config.criterial
        let highestLevel = -Infinity
        let chosenLabel = null
        let chosenOrder = null

        for (const m of measurements) {
            const criterial = handleCriteria(conf, m.name, m.value)
            if (criterial && criterial.level > highestLevel) {
                highestLevel = criterial.level
                chosenLabel = criterial.label
                chosenOrder = criterial.order_level
            }
        }

        if (chosenLabel) {
            return {
                label: chosenLabel,
                order: chosenOrder,
                level: highestLevel,
            }
        }

        return {
            label: '--',
            order: 1,
            level: -1,
        }
    }

    return { format }
}

export const useGetStatsOfMeasure = () => {
    const { selectedSystem } = useAuth()
    const [data, setData] = useState(null)
    const { start_date, end_date } = useGetStartAndEndDate()

    useEffect(() => {
        const fn = async () => {
            const res = await axios.get(
                `/api/analyzer/count_of_measure/${
                    selectedSystem?.system_id || 'default'
                }`,
                {
                    params: {
                        start_date,
                        end_date,
                    },
                }
            )
            setData(res.data?.data || null)
        }
        fn()
    }, [selectedSystem, start_date, end_date])
    return { data }
}

export const useGetCriteriaLevelColor = () => {
    const colorsLevel = {
        0: '#2ecc71', // ดีมาก
        1: '#27ae60', // ดี
        2: '#f1c40f', // ปกติ
        3: '#e67e22', // ไม่ปกติ
        4: '#e74c3c', // ผิดปกติ
        5: '#c0392b', // แย่มาก
        6: '#7f8c8d', // แย่สุด/วิกฤติ
    }
    const format = (level) => {
        return colorsLevel[level] || '#bdc3c7'
    }
    return { format }
}

export const useTranslateMeasurements = () => {
    const translate = {
        'HEIGHT,WEIGHT,BMI': 'น้ำหนัก ส่วนสูง ดัชนีมวลกาย',
        'ALCOLHOL,SMOKE': 'การดื่มสุรา และการสูบบุหรี่',
        'SMOKE,ALCOLHOL': 'การดื่มสุรา และการสูบบุหรี่',
        DEPRESSION: 'ภาวะสุขภาพจิต',
        GLU: 'น้ำตาลในเลือด',
        'SYS,DIA,PUL': 'ความดันโลหิต',
        'SPO2,PUL': 'ค่าออกซิเจนในเลือด',
        SALINITY: 'ระดับการกินเค็ม',
        TEMP: 'อุณหภูมิร่างกาย',
        HEIGHT: 'ส่วนสูง',
        WEIGHT: 'น้ำหนัก',
        BMI: 'ค่าดัชนีมวลกาย',
        ALCOLHOL: 'การดื่มสุรา',
        SMOKE: 'การสูบบุหรี่',
        SYS: 'ความดันโลหิต (SYS)',
        DIA: 'ความดันโลหิต (DIA)',
        PUL: 'ชีพจร (PUL)',
        SPO2: 'ค่าออกซิเจนในเลือด (SPO2)',
    }
    const format = (key) => {
        if (key in translate) {
            return translate[key]
        }
        return key
    }
    return { format }
}

export const mapMeasurementUnit = () => {
    const map = {
        'HEIGHT,WEIGHT,BMI': 'cm/kg/bmi',
        'ALCOLHOL,SMOKE': 'ครั้ง/วัน',
        'SMOKE,ALCOLHOL': 'ครั้ง/วัน',
        DEPRESSION: 'คะแนน',
        GLU: 'mg/dL',
        'SYS,DIA,PUL': 'mmHg/bpm',
        'SPO2,PUL': '%',
        SALINITY: 'mEq/L',
        TEMP: '°C',
        HEIGHT: 'cm',
        WEIGHT: 'kg',
        BMI: 'kg/m2',
        ALCOLHOL: 'ครั้ง/วัน',
        SMOKE: 'ครั้ง/วัน',
        SYS: 'mmHg',
        DIA: 'mmHg',
        PUL: 'bpm',
        SPO2: '%',
    }
    const format = (key) => {
        if (key in map) {
            return map[key]
        }
        return key
    }
    return { format }
}

export const useGetStartAndEndDate = () => {
    const { start_date, end_date, page, limit, search, order } = useSelector(
        (state) => state.analyzer
    )
    return { start_date, end_date, page, limit, search, order }
}

export const useSetStartDate = () => {
    const dispatch = useDispatch()

    return (start_date) => {
        dispatch({
            type: SET_START_DATE_ANALYZER_DATE_TIME,
            payload: start_date,
        })
    }
}

export const useSetEndDate = () => {
    const dispatch = useDispatch()

    return (end_date) => {
        dispatch({
            type: SET_END_DATE_ANALYZER_DATE_TIME,
            payload: end_date,
        })
    }
}

export const useAnalyzerStore = () => {
    const { start_date, end_date, page, limit, search, order, update } =
        useSelector((state) => state.analyzer)

    const dispatch = useDispatch()

    const setStartDate = (start_date) => {
        dispatch({
            type: SET_START_DATE_ANALYZER_DATE_TIME,
            payload: start_date,
        })
    }
    const setEndDate = (end_date) => {
        dispatch({
            type: SET_END_DATE_ANALYZER_DATE_TIME,
            payload: end_date,
        })
    }
    const setPage = (page) => {
        console.log(page)
        dispatch({
            type: SET_PAGE_ANALYZER_MEASUREMENT,
            payload: page,
        })
    }
    const setLimit = (limit) => {
        dispatch({
            type: SET_LIMIT_ANALYZER_MEASUREMENT,
            payload: limit,
        })
    }
    const setSearch = (search) => {
        dispatch({
            type: SET_SEARCH_ANALYZER_MEASUREMENT,
            payload: search,
        })
    }
    const setOrder = (search) => {
        dispatch({
            type: SET_ORDER_ANALYZER_MEASUREMENT,
            payload: search,
        })
    }
    const setConfig = (config) => {
        dispatch({
            type: SET_CONFIG_ANALYZER_MEASUREMENT,
            payload: config,
        })
    }
    const setConfigUpdate = (fn) => {
        dispatch({
            type: SET_CONFIG_UPDATE_ANALYZER_MEASUREMENT,
            payload: fn(update),
        })
    }

    return {
        update,
        start_date,
        end_date,
        page,
        limit,
        search,
        order,
        setPage,
        setLimit,
        setSearch,
        setOrder,
        setEndDate,
        setStartDate,
        setConfig,
        setConfigUpdate,
    }
}

export const useGetMeasurement = () => {
    const { selectedSystem } = useAuth()
    const [data, setData] = useState(null)
    const { search, limit, page, order, start_date, end_date } =
        useAnalyzerStore()

    useEffect(() => {
        const fn = async () => {
            if (!selectedSystem?.system_id) return
            const res = await axios.get(
                `/api/analyzer/measurement/${selectedSystem?.system_id}`,
                {
                    params: {
                        search,
                        limit,
                        page,
                        order,
                        start_date,
                        end_date,
                    },
                }
            )
            setData(res.data || null)
        }
        fn()
    }, [selectedSystem, search, limit, page, order, start_date, end_date])
    return { data }
}
